import { customElement, scss } from '../../core/ids-decorators';
import { attributes, htmlAttributes } from '../../core/ids-attributes';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';
import { ALIGNMENT_TYPES, applyContentAlignmentClass } from './ids-accordion-common';

import IdsColorVariantMixin from '../../mixins/ids-color-variant-mixin/ids-color-variant-mixin';
import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsKeyboardMixin from '../../mixins/ids-keyboard-mixin/ids-keyboard-mixin';
import IdsElement from '../../core/ids-element';

import IdsAccordionHeader from './ids-accordion-header';
import styles from './ids-accordion-panel.scss';

const Base = IdsColorVariantMixin(
  IdsKeyboardMixin(
    IdsEventsMixin(
      IdsElement
    )
  )
);

/**
 * IDS Accordion Panel Component
 * @type {IdsAccordionPanel}
 * @inherits IdsElement
 * @mixes IdsColorVariantMixin
 * @mixes IdsKeyboardMixin
 * @mixes IdsEventsMixin
 */
@customElement('ids-accordion-panel')
@scss(styles)
export default class IdsAccordionPanel extends Base {
  paneOpenListener?: () => void;

  paneCloseListener?: () => void;

  constructor() {
    super();
    this.state = {};
  }

  connectedCallback() {
    super.connectedCallback();
    this.#attachEventHandlers();
    this.#refreshContentAlignment(this.contentAlignment);
    this.#toggleExpanderDisplay();
    this.pane?.style.setProperty('height', this.expanded ? 'auto' : '0px');
    if (this.expanded) this.pane?.classList.add('expanded');
    this.#refreshDisabled();
  }

  /**
   * Syncs disabled state to the header when set before it is appended.
   */
  #refreshDisabled(): void {
    const isDisabled = stringToBool(this.getAttribute(attributes.DISABLED));
    if (isDisabled && this.header) {
      this.header.disabled = true;
    }
    const paneContent = this.shadowRoot?.querySelector('.ids-accordion-pane-content') as HTMLElement | null;
    if (paneContent && isDisabled) {
      paneContent.inert = true;
    }
  }

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array} The attributes in an array
   */
  static get attributes() {
    return [
      ...super.attributes,
      attributes.DISABLED,
      attributes.EXPANDED
    ];
  }

  /**
   * @returns {Array<string>} List of available color variants for this component
   */
  colorVariants = [
    'app-menu',
    'sub-app-menu',
    'module-nav',
    'sub-module-nav'
  ];

  vetoableEventTypes = ['beforeexpanded', 'beforecollapsed'];

  /**
   * When the accordion's color variant is set, push this change through to include a check on the expander icon
   */
  onColorVariantRefresh() {
    this.#toggleExpanderDisplay();
  }

  /**
   * Sets a CSS class containing alignment rules for text/icons/images on this accordion panel
   * @param {string|null} val the new alignment rule to set
   */
  set contentAlignment(val) {
    let thisAlignment = null;
    if (ALIGNMENT_TYPES.includes(val)) {
      thisAlignment = val;
    }

    if (this.state.contentAlignment !== thisAlignment) {
      this.state.contentAlignment = thisAlignment;
      this.#refreshContentAlignment(thisAlignment);
      if (this.header) {
        this.header?.refreshContentAlignment(thisAlignment);
      }
    }
  }

  /**
   * @returns {string|null} representing how icons/text/images are currently aligned
   */
  get contentAlignment() {
    return this.state.contentAlignment;
  }

  /**
   * Visually updates the alignment of icons/text/images in the accordion panel
   * @param {*} thisAlignment the alignment rule to set
   */
  #refreshContentAlignment(thisAlignment = null) {
    if (this.container) {
      applyContentAlignmentClass(this.container?.classList, thisAlignment);
      this.container.classList[this.hasParentPanel ? 'add' : 'remove']('is-child-panel');
    }
  }

  /**
   * @readonly
   * @returns {HTMLElement} the parent accordion component
   */
  get accordion(): any {
    return this.closest('ids-accordion');
  }

  /**
   * @readonly
   * @returns {HTMLElement|null} the provided header, if applicable
   */
  get header(): IdsAccordionHeader | null {
    return this.querySelector<IdsAccordionHeader>('[slot="header"]');
  }

  /**
   * @readonly
   * @returns {HTMLElement|null} the expander button
   */
  get expander(): HTMLElement | undefined | null {
    return this.container?.querySelector('.ids-accordion-panel-expander');
  }

  /**
   * @readonly
   * @returns {HTMLElement|null} the inner expand/collapse pane element
   */
  get pane(): HTMLElement | undefined | null {
    return this.container?.querySelector('.ids-accordion-pane');
  }

  /**
   * @readonly
   * @returns {IdsAccordionPanel[]} child accordion panels nested in this panel, if applicable
   */
  get childPanels(): IdsAccordionPanel[] {
    const panels = this.querySelectorAll<IdsAccordionPanel>('ids-accordion-panel');
    return panels.length ? [...panels] : [];
  }

  /**
   * @readonly
   * @returns {boolean} true if this pane resides inside another pane
   */
  get hasParentPanel(): boolean {
    return this.parentElement?.tagName === 'IDS-ACCORDION-PANEL';
  }

  /**
   * @readonly
   * @returns {boolean} true if this pane resides in an expanded parent pane
   */
  get parentExpanded(): boolean {
    return Boolean(this.hasParentPanel && (this.parentElement as IdsAccordionPanel)?.expanded);
  }

  /**
   * @readonly
   * @returns {boolean} true if this accordion panel has child content
   * (aside from its header) and can be expanded/collapsed
   */
  get isExpandable(): boolean {
    return [...this.children].length > 1;
  }

  #expanded = false;

  /**
   * Set the expanded property
   * @param {boolean | string} value true/false
   */
  set expanded(value: boolean | string) {
    const isValueTruthy = stringToBool(value);
    const currentValue = this.#expanded;

    if (currentValue === isValueTruthy) return;

    if (isValueTruthy) {
      this.#expanded = true;
      this.setAttribute(attributes.EXPANDED, `${value}`);
      this.#toggleExpanded(true);
    } else {
      this.#expanded = false;
      this.removeAttribute(attributes.EXPANDED);
      this.#toggleExpanded(false);
    }
  }

  /**
   * Get the expanded property
   * @returns {boolean | string} the expanded property
   */
  get expanded(): boolean | string {
    return stringToBool(this.getAttribute(attributes.EXPANDED));
  }

  /**
   * The main state switching function
   * Collapses sibling panels if allowOnePane setting is true
   * @param {boolean} isExpanded true if the panel is to be expanded
   * @returns {void}
   * @private
   */
  #toggleExpanded(isExpanded: boolean): void {
    this.header?.setAttribute(htmlAttributes.ARIA_EXPANDED, `${isExpanded}`);

    if (!isExpanded) {
      this.collapsePane();
    } else {
      this.expandPane();
      if (this.accordion?.allowOnePane) {
        this.#collapseSiblingPanels();
      }
    }
  }

  /**
   * Collapses sibling ids-accordion-panels
   * @returns {void}
   * @private
   */
  #collapseSiblingPanels(): void {
    const panels = [...this.parentElement?.querySelectorAll<IdsAccordionPanel>(':scope > ids-accordion-panel') ?? []];

    panels.forEach((panel) => {
      if (panel !== this && panel.expanded) {
        panel.expanded = false;
      }
    });
  }

  /**
   * Toggles expansion on this pane, and selects its header
   * @returns {void}
   */
  #selectAndToggle(): void {
    this.expanded = !this.expanded;
    this.select(this);

    const toCloseChildren = stringToBool(this.parentElement!.getAttribute('collapse-children'));

    if (toCloseChildren) {
      for (const panel of this.childPanels) {
        panel.expanded = false;
      }
    }
  }

  /**
   * Hides/Shows an Accordion Header's expander icon
   * @returns {void}
   */
  #toggleExpanderDisplay(): void {
    if (this.header instanceof IdsAccordionHeader) {
      this.header.toggleExpanderIcon(this.isExpandable);
    }
  }

  /**
   * @returns {boolean} true if this panel appears "nested"
   */
  get nested(): boolean {
    return !!this.container?.classList?.contains('nested');
  }

  /**
   * @param {boolean} val true if this panel should appear "nested"
   */
  set nested(val: boolean) {
    this.container?.classList[stringToBool(val) ? 'add' : 'remove']('nested');
  }

  /**
   * Sets disabled property
   * @param {boolean | string} value true/false
   */
  set disabled(value: boolean | string) {
    const disabled = stringToBool(value);
    if (this.header) this.header.disabled = disabled;

    if (disabled) {
      this.setAttribute(attributes.DISABLED, `${disabled}`);
      this.setAttribute(attributes.TABINDEX, '-1');
    } else {
      this.removeAttribute(attributes.DISABLED);
      this.removeAttribute(attributes.TABINDEX);
    }

    const paneContent = this.shadowRoot?.querySelector('.ids-accordion-pane-content') as HTMLElement | null;
    if (paneContent) {
      paneContent.inert = disabled;
    }
  }

  get disabled(): boolean | string {
    return stringToBool(this.getAttribute(attributes.DISABLED));
  }

  /**
   * Collapse the expandable area pane.
   * @private
   * @returns {void}
   */
  collapsePane(): void {
    requestAnimationFrame(() => {
      if (!this.pane) {
        return;
      }

      // Remove any pre-existing Open listener that may still be in progress
      if (this.paneOpenListener) {
        this.pane.removeEventListener('transitionend', this.paneOpenListener);
        delete this.paneOpenListener;
      }

      this.pane.style.height = `${this.pane.scrollHeight}px`;
      this.container?.classList.remove('expanded');

      if (this.header) {
        this.header.expanded = false;
      }

      requestAnimationFrame(() => {
        if (!this.pane) {
          return;
        }

        // Setting height to "0" kicks off animation
        this.pane.style.height = `0px`;
        this.paneCloseListener = () => {
          if (this.pane) {
            this.pane.style.display = 'none';
          }
          if (this.header) {
            this.header.triggerEvent('aftercollapsed', this.header, { bubbles: true });
          }
        };
        this.pane.classList.remove('expanded');
        this.pane.addEventListener('transitionend', this.paneCloseListener, { once: true });
      });
    });
  }

  /**
   * Expand the expandable area pane.
   * @private
   * @returns {void}
   */
  expandPane(): void {
    if (!this.pane || !this.isExpandable) {
      return;
    }

    // Remove any pre-existing Close listener that may still be in progress
    if (this.paneCloseListener) {
      this.pane.removeEventListener('transitionend', this.paneCloseListener);
      delete this.paneCloseListener;
    }

    this.pane.style.display = 'block';

    const paneExpanded = (): void => {
      this.pane?.classList.add('expanded');

      // Fire afterexpanded event after animation completes
      if (this.header) {
        this.header.triggerEvent('afterexpanded', this.header, { bubbles: true });
      }
    };

    requestAnimationFrame(() => {
      this.container?.classList.add('expanded');

      if (!this.pane) return;

      // Expand header component
      if (this.header) {
        this.header.expanded = true;
      }

      const isHidden = this.pane.offsetParent === null;

      // If hidden, set final state immediately without animation
      if (isHidden) {
        this.pane.style.height = 'auto';
        paneExpanded();
        return;
      }

      const currentHeight = this.pane.style.height;
      const isAutoOrEmpty = currentHeight === 'auto' || currentHeight === '';

      // Already fully expanded — skip animation to avoid resetting height
      // and causing a visual jump when content is dynamically added via slot
      if (isAutoOrEmpty) {
        paneExpanded();
        return;
      }

      // Setting height kicks off animation
      this.pane.style.height = `${this.pane.scrollHeight}px`;

      this.paneOpenListener = () => {
        // NOTE: `auto` height allows for nested accordions to expand
        // when their content is displayed
        if (this.pane) {
          this.pane.style.height = 'auto';
        }
        paneExpanded();
      };
      this.pane.addEventListener('transitionend', this.paneOpenListener, { once: true });
    });
  }

  /**
   * Sets up event listeners
   * @private
   * @returns {void}
   */
  #attachEventHandlers(): void {
    this.offEvent('click', this.expander);
    this.onEvent('click', this.expander, (e: MouseEvent) => {
      if (this.disabled) return;

      const clickedIcon = e.composedPath()
        .some((el: EventTarget) => (el as HTMLElement).classList?.contains('ids-accordion-expander-icon'));

      if (!this.toggleOnIcon || clickedIcon) {
        this.#selectAndToggle();
      }
    });

    this.listen('Enter', this.expander, (e: KeyboardEvent) => {
      e.stopPropagation();
      if (!this.disabled && e.target === this.header) {
        this.#selectAndToggle();
      }
    });

    this.listen(' ', this.expander, (e: KeyboardEvent) => {
      e.stopPropagation();
      if (!this.disabled && e.target === this.header) {
        this.#selectAndToggle();
      }
    });

    this.offEvent('touchend', this.expander);
    this.onEvent('touchend', this.expander, (e: { touches: string | any[]; }) => {
      if (e.touches && e.touches.length > 0) {
        this.#selectAndToggle();
      }
    }, { passive: true });

    // listen to header slot changes
    this.offEvent('slotchange.header-slotchange');
    this.onEvent('slotchange.header-slotchange', this.container?.querySelector('slot[name="header"]'), () => {
      // Add aria-expanded only if this panel is expandable
      if (this.header && this.isExpandable) {
        this.header?.setAttribute(htmlAttributes.ARIA_EXPANDED, `${this.expanded}`);
      } else {
        this.header?.removeAttribute(htmlAttributes.ARIA_EXPANDED);
      }

      this.#toggleExpanderDisplay();
    });

    // listen to content slot change
    const contentSlot = this.container?.querySelector<HTMLSlotElement>('slot[name="content"]');
    this.offEvent('slotchange.content-slotchange');
    this.onEvent('slotchange.content-slotchange', contentSlot, () => {
      if (this.expanded) this.#toggleExpanded(true);
      this.header?.toggleExpanderIcon?.(!!contentSlot?.assignedElements().length);
    });

    // Stops the selected event from bubbling up to the accordion
    this.offEvent('selected.accordion-panel-selected', this);
    this.onEvent('selected.accordion-panel-selected', this, (e: Event) => {
      if ((e.target as HTMLElement).nodeName.startsWith('IDS-ACCORDION')) e.stopPropagation();
    });
  }

  /**
   * Select the prev/next panel
   * @param {IdsAccordionPanel} panel The panel to be selected
   * @returns {void}
   */
  select(panel: this): void {
    if (panel?.tagName === 'IDS-ACCORDION-PANEL') {
      if (this.header) this.header.selected = true;
      panel.focus();
    }
  }

  /**
   * Passes focus from the Panel to its Header component
   * @returns {void}
   */
  focus(): void {
    this.header?.focus();
  }

  /**
   * @returns {boolean} true if toggle on icon mode is enabled
   */
  get toggleOnIcon(): boolean {
    return this.accordion?.toggleOnIcon ?? false;
  }

  /**
   * Inner template contents
   * @returns {string} The template
   */
  template(): string {
    return `
      <div class="ids-accordion-panel">
        <div class="ids-accordion-panel-expander">
          <slot name="header"></slot>
        </div>
        <div class="ids-accordion-pane">
          <div class="ids-accordion-pane-content">
            <slot name="content"></slot>
          </div>
        </div>
      </div>
    `;
  }
}
